import React from "react";
import { Link } from "react-router-dom";
import {
  Droplets,
  Gauge,
  Factory,
  ArrowRight,
  CheckCircle2,
  Wrench,
} from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";

import slurry from "../images/slurry.png";
import wastewater from "../images/wastewater.png";
import dewatering from "../images/dewatering.png";
import heavyslurry from "../images/heavu slurry.png";
import booster from "../images/booster.png";

const pumps = [
  {
    title: "Dewatering Pumps",
    image: dewatering,
    desc: "Portable and heavy-duty pumps for removing water from construction sites, pits, mines, and flooded areas.",
    icon: Droplets,
  },
  {
    title: "Wastewater Pumps",
    image: wastewater,
    desc: "Reliable pumps designed for municipal wastewater treatment, sewage transfer, and dirty water handling.",
    icon: Factory,
  },
  {
    title: "Slurry Pumps",
    image: slurry,
    desc: "Heavy-duty pumps designed for abrasive slurry handling in mining, process plants, and industrial operations.",
    icon: Wrench,
  },
  {
    title: "Heavy Slurry Pumps",
    image: heavyslurry,
    desc: "Extreme-duty pumps capable of handling high-density slurry and demanding mining conditions.",
    icon: Wrench,
  },
  {
    title: "Booster Pumps",
    image: booster,
    desc: "Pressure boosting pumps used in buildings, water supply systems, treatment plants, and distribution networks.",
    icon: Gauge,
  },
  {
    title: "Industrial Pumps",
    image:
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?auto=format&fit=crop&w=1200&q=80",
    desc: "High-performance industrial pumps for chemical plants, treatment plants, utilities, and large facilities.",
    icon: Factory,
  },
];

const highlights = [
  "Engineered for reliable flow, pressure, and transfer performance",
  "Serving Kolkata, Newtown, Rajarhat, and across West Bengal",
  "Integrated support for selection, installation, and maintenance",
];

export default function PumpsPage() {
  return (
    <>
      <SEO
        title="Water Pump Systems in Kolkata, Newtown, Rajarhat, West Bengal | Booster, Dewatering & Industrial Pumps"
        description="Hydrotec Solutions supplies and installs booster pumps, dewatering pumps, slurry pumps, wastewater pumps, and industrial pumps in Kolkata, Newtown, Rajarhat, and across West Bengal. Reliable pump solutions for water infrastructure and industry."
        keywords="water pump Kolkata, booster pump Kolkata, dewatering pump West Bengal, slurry pump Kolkata, wastewater pump Rajarhat, industrial pump Newtown, pump supplier West Bengal, pump installation Kolkata"
        canonical="https://hydroteclimited.com/solutions/pumps"
      />
      <PageHero
        title="Pump Systems"
        text="Hydrotec provides high-performance pump solutions for dewatering, wastewater, slurry handling, pressure boosting, and industrial applications."
        image={booster}
        primaryText="Request Consultation"
        primaryLink="/contact"
        secondaryText="View Projects"
        secondaryLink="/projects"
      />

      <section className="bg-white text-slate-950">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:px-8 lg:grid-cols-[1fr_1fr] lg:px-10 lg:py-28">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Pump Solutions
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Built for movement, pressure, and dependable performance.
            </h2>
          </div>

          <div>
            <p className="text-lg leading-8 text-slate-600">
              Our pump systems are selected for real operating conditions across
              water infrastructure, wastewater, industrial processing, and
              demanding site environments. From compact booster units to
              heavy-duty slurry pumps, Hydrotec supports practical deployment
              with performance-focused design.
            </p>

            <div className="mt-8 space-y-3">
              {highlights.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-cyan-700" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 text-slate-950">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-10 lg:py-28">
          <div className="mb-14 text-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Pump Categories
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              Explore our pump range
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-600">
              Designed for dewatering, wastewater, slurry movement, boosting,
              and industrial performance across multiple applications.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {pumps.map((pump) => {
              const Icon = pump.icon;
              return (
                <div
                  key={pump.title}
                  className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="overflow-hidden">
                    <img
                      src={pump.image}
                      alt={pump.title}
                      className="h-72 w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-7">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
                      <Icon className="h-6 w-6" />
                    </div>

                    <h3 className="mt-5 text-2xl font-semibold text-slate-900">
                      {pump.title}
                    </h3>

                    <p className="mt-3 leading-7 text-slate-600">{pump.desc}</p>

                    <Link
                      to="/contact"
                      className="mt-6 inline-flex items-center text-sm font-semibold text-cyan-700 transition hover:text-cyan-800"
                    >
                      View Pumps <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-24 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-28">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-white/55">
              Why Hydrotec
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              More than pumps. A complete infrastructure mindset.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/72">
              Hydrotec supports pump systems as part of a larger water ecosystem
              that may include treatment, storage, dosing, automation, and
              monitoring. This helps create better-performing deployments with
              stronger long-term reliability.
            </p>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm">
            <h3 className="text-2xl font-semibold text-white">
              Need the right pump system for your project?
            </h3>
            <p className="mt-4 leading-8 text-white/70">
              Talk to Hydrotec about dewatering pumps, wastewater pumps, slurry
              pumps, heavy-duty industrial pumps, and booster systems.
            </p>

            <Link
              to="/contact"
              className="mt-8 inline-flex items-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
            >
              Request Consultation <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}